import api from './client'

/** Rows pulled per infinite-scroll step. */
export const EXAMS_PAGE_SIZE = 10

/**
 * GET /api/exams/ — one page of exams.
 *
 * The catalogue can run to thousands of rows, so the list is consumed a page
 * at a time. The server owns ordering and the `status` filter; a page here is
 * already sorted "most actionable first" and can be appended as-is.
 *
 * Resolves to `{ results, hasMore }`.
 */
export async function listExamsPage({ page = 1, status, subject, signal } = {}) {
  const { data } = await api.get('/api/exams/', {
    params: {
      page,
      page_size: EXAMS_PAGE_SIZE,
      ...(status && status !== 'all' ? { status } : {}),
      ...(subject ? { subject } : {}),
    },
    signal,
  })
  if (Array.isArray(data)) return { results: data, hasMore: false }
  return { results: data.results ?? [], hasMore: Boolean(data.next) }
}

/**
 * GET /api/exams/summary/ — filter-chip counts and subject buckets over the
 * whole visible set. Aggregated in SQL, so it stays cheap as the catalogue
 * grows and doesn't depend on which pages the client has loaded.
 */
export async function getExamsSummary({ subject, signal } = {}) {
  const { data } = await api.get('/api/exams/summary/', {
    params: subject ? { subject } : {},
    signal,
  })
  return data
}

/** GET /api/exams/ — single (first) page, raw axios response. */
export const listExams = () => api.get('/api/exams/')

/** GET /api/exams/:code/ */
export const getExam = (code) => api.get(`/api/exams/${code}/`)

/** POST /api/exams/:code/start/ */
export const startExam = (code) => api.post(`/api/exams/${code}/start/`)

/** POST /api/exams/:code/save-answer/ */
export const saveAnswer = (code, questionId, answer) =>
  api.post(`/api/exams/${code}/save-answer/`, {
    question_id: questionId,
    answer,
  })

/** POST /api/exams/:code/finish/ */
export const finishExam = (code) => api.post(`/api/exams/${code}/finish/`)

/** GET /api/exams/:code/results/ */
export const getResults = (code) => api.get(`/api/exams/${code}/results/`)

/** GET /api/exams/:code/leaderboard/?live=1 */
export const getLeaderboard = (code, live = false) =>
  api.get(`/api/exams/${code}/leaderboard/`, live ? { params: { live: 1 } } : undefined)

/** GET /api/exams/:code/my-result/?result_id=<int> */
export const getMyResult = (code, resultId) =>
  api.get(`/api/exams/${code}/my-result/`, resultId ? { params: { result_id: resultId } } : undefined)
